import * as Phaser from 'phaser';

/** Procedural geometric textures — original, no external art */
export function generateTextures(scene: Phaser.Scene): void {
  const g = scene.make.graphics({ x: 0, y: 0 });

  // floor tile
  g.clear();
  g.fillStyle(0x272139, 1);
  g.fillRect(0, 0, 64, 64);
  g.lineStyle(1, 0x6d5f88, 0.55);
  g.strokeRect(0.5, 0.5, 63, 63);
  g.fillStyle(0x322a48, 1);
  g.fillRect(0, 0, 32, 10);
  g.fillRect(32, 10, 32, 10);
  g.fillStyle(0xffd84a, 0.28);
  g.fillRect(0, 0, 32, 4);
  g.generateTexture('tex_floor', 64, 64);

  // BUDDY — oversized, readable toy robot
  g.clear();
  g.lineStyle(4, 0x171322, 1);
  g.fillStyle(0x55e5ff, 1);
  g.fillRoundedRect(6, 15, 58, 49, 16);
  g.strokeRoundedRect(6, 15, 58, 49, 16);
  g.lineStyle(4, 0x171322, 1);
  g.lineBetween(35, 15, 35, 7);
  g.fillStyle(0xffd84a, 1);
  g.fillCircle(35, 6, 6);
  g.lineStyle(3, 0x171322, 1);
  g.strokeCircle(35, 6, 6);
  g.fillStyle(0x171322, 1);
  g.fillCircle(24, 35, 7);
  g.fillCircle(47, 35, 7);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(26, 33, 2.5);
  g.fillCircle(49, 33, 2.5);
  g.lineStyle(4, 0x171322, 1);
  g.beginPath();
  g.arc(35, 43, 12, 0.2, Math.PI - 0.2, false);
  g.strokePath();
  g.fillStyle(0xff5f87, 1);
  g.fillRoundedRect(14, 60, 16, 10, 4);
  g.fillRoundedRect(41, 60, 16, 10, 4);
  g.generateTexture('tex_bot', 70, 72);

  // particle spark
  g.clear();
  g.fillStyle(0xffd166, 1);
  g.fillCircle(4, 4, 4);
  g.generateTexture('tex_spark', 8, 8);

  // confetti bits
  g.clear();
  g.fillStyle(0xff6b6b, 1);
  g.fillRect(0, 0, 6, 4);
  g.generateTexture('tex_confetti_r', 6, 4);
  g.clear();
  g.fillStyle(0x4ecdc4, 1);
  g.fillRect(0, 0, 6, 4);
  g.generateTexture('tex_confetti_c', 6, 4);
  g.clear();
  g.fillStyle(0xffe66d, 1);
  g.fillRect(0, 0, 6, 4);
  g.generateTexture('tex_confetti_y', 6, 4);

  g.destroy();
}

export function drawToolGraphics(
  scene: Phaser.Scene,
  toolId: string,
  color: number,
  w: number,
  h: number,
  shape: string,
): Phaser.GameObjects.Graphics {
  const gfx = scene.add.graphics();
  gfx.fillStyle(color, 1);
  gfx.lineStyle(2, 0xffffff, 0.35);

  switch (shape) {
    case 'circle':
      gfx.fillCircle(0, 0, Math.min(w, h) / 2);
      gfx.strokeCircle(0, 0, Math.min(w, h) / 2);
      if (toolId === 'fan') {
        gfx.lineStyle(3, 0x1a1f2e, 0.5);
        for (let i = 0; i < 4; i++) {
          const a = (i * Math.PI) / 2;
          gfx.lineBetween(0, 0, Math.cos(a) * 14, Math.sin(a) * 14);
        }
      } else if (toolId === 'magnet') {
        gfx.lineStyle(4, 0xff6b6b, 0.9);
        gfx.beginPath();
        gfx.arc(0, 0, 10, Math.PI * 0.15, Math.PI * 0.85, false);
        gfx.strokePath();
        gfx.lineStyle(4, 0x4ecdc4, 0.9);
        gfx.beginPath();
        gfx.arc(0, 0, 10, Math.PI * 1.15, Math.PI * 1.85, false);
        gfx.strokePath();
      } else if (toolId === 'portal_gate') {
        gfx.lineStyle(4, 0xffffff, 0.6);
        gfx.strokeCircle(0, 0, Math.min(w, h) / 2 - 6);
      } else if (toolId === 'bumper') {
        gfx.fillStyle(0xffffff, 0.35);
        gfx.fillCircle(0, 0, 8);
      }
      break;
    case 'wedge': {
      const hw = w / 2;
      const hh = h / 2;
      gfx.beginPath();
      gfx.moveTo(-hw, hh);
      gfx.lineTo(hw, hh);
      gfx.lineTo(hw, -hh);
      gfx.closePath();
      gfx.fillPath();
      gfx.strokePath();
      break;
    }
    case 'capsule': {
      const hw = w / 2;
      const hh = h / 2;
      gfx.fillRoundedRect(-hw, -hh, w, h, Math.min(hw, hh));
      gfx.strokeRoundedRect(-hw, -hh, w, h, Math.min(hw, hh));
      if (toolId === 'rocket') {
        gfx.fillStyle(0xff5d6c, 1);
        gfx.fillTriangle(0, hh - 2, -8, hh + 10, 8, hh + 10);
      } else if (toolId === 'balloon') {
        gfx.lineStyle(2, 0xffffff, 0.5);
        gfx.lineBetween(0, hh, 0, hh + 10);
      }
      break;
    }
    default: {
      const hw = w / 2;
      const hh = h / 2;
      gfx.fillRoundedRect(-hw, -hh, w, h, 6);
      gfx.strokeRoundedRect(-hw, -hh, w, h, 6);
      if (toolId === 'spring') {
        gfx.lineStyle(2, 0x1a1f2e, 0.55);
        for (let i = -2; i <= 2; i++) {
          gfx.lineBetween(-hw + 8, i * 5, hw - 8, i * 5);
        }
      } else if (toolId === 'bounce_pad') {
        gfx.fillStyle(0xffffff, 0.35);
        gfx.fillRoundedRect(-hw + 6, -hh + 4, w - 12, 6, 3);
      } else if (toolId === 'crate') {
        gfx.lineStyle(2, 0x1a1f2e, 0.35);
        gfx.strokeRect(-hw + 8, -hh + 8, w - 16, h - 16);
        gfx.lineBetween(-hw + 8, -hh + 8, hw - 8, hh - 8);
        gfx.lineBetween(hw - 8, -hh + 8, -hw + 8, hh - 8);
      }
      break;
    }
  }
  return gfx;
}
